import * as React from 'react';
import {
  Button, TextField, Dialog,
  DialogActions, DialogContent,
  DialogTitle, Grid, Typography,
  Box, CircularProgress,
} from '@material-ui/core';
import type { GridColDef } from '@material-ui/data-grid';
import { DataGrid } from '@material-ui/data-grid';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';

interface WarrantyDialogProps {
  barcode: string,
  showModal: boolean,
  toggleModal: (boolean: boolean) => void,
  addWarranty: (warrantyId: string) => void
}
interface Warranty {
  warrantyId: string;
  lastName: string;
  firstName: string;
  phoneNumber: string;
  warrantyNumber: number;
  warrantyProvider: string;
}

const WarrantyDialog: React.FC<WarrantyDialogProps> = ({
  barcode = 'Missing Number', showModal, toggleModal, addWarranty,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [showInput, setShowInput] = React.useState(true);
  const [warrantyData, setWarrantyData] = React.useState<Warranty[] | undefined>([]);
  const [warranty, setWarranty] = React.useState<Warranty | undefined>();
  const [warrantyInputs, setWarrantyInputs] = React.useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    warrantyNumber: '',
  });
  const [fetching, setFetching] = React.useState(false);

  const columns: GridColDef[] = [
    { field: 'id', hide: true },
    { field: 'firstName', headerName: 'First name', width: 130 },
    { field: 'lastName', headerName: 'Last name', width: 130 },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      type: 'number',
      width: 180,
    },
    {
      field: 'warrantyNumber',
      headerName: 'Warranty Number',
      type: 'number',
      width: 200,
    },
  ];

  const handleWarrantyInputs = (event: React.ChangeEvent<{ name?: string | undefined; value: unknown; type?: string; checked?: boolean }>) => {
    if (typeof event?.target.value === 'string' && typeof event?.target.name === 'string') {
      setWarrantyInputs({
        ...warrantyInputs,
        [event.target.name]: event.target.value,
      });
    }
  };

  const searchWarranties = () => {
    setFetching(true);
    axios.get('/marker/warrantyquery', {
      params: {
        ...warrantyInputs,
      },
    })
      .then((response: { data: Warranty[]; }) => {
        setWarrantyData(response.data);
        setShowInput(false);
      })
      .catch((error: AxiosError): void => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      })
      .finally(() => {
        setFetching(false);
      });
  };

  const handleWarrantySearch = () => {
    searchWarranties();
  };

  const transitionToInput = () => {
    setShowInput(true);
    setWarranty(undefined);
    setWarrantyData([]);
  };

  const handleSaveWarranty = () => {
    if (warranty?.warrantyId) {
      addWarranty(warranty.warrantyId);
      toggleModal(false);
    } else {
      enqueueSnackbar('A warranty must be selected.', { persist: false, variant: 'error' });
    }
  };

  return (
    <Dialog open={showModal} aria-labelledby="form-warranty" fullWidth maxWidth="lg">
      <DialogTitle id="form-dialog-title">
        <Typography variant="overline">{`CURRENT REPAIR ITEM · #${barcode}`}</Typography>
        <Typography variant="h5" id="dialog-header">
          {showInput
            ? 'Refine Warranty options with the filters below'
            : 'Warranty Results based on previous filter set'}
        </Typography>

      </DialogTitle>
      <DialogContent style={{ height: '33vh' }}>
        <Grid container spacing={2} alignContent-="center" alignItems="center">
          {showInput
            ? (
              <>
                <Grid item xs={4}>
                  <TextField
                    id="firstName"
                    name="firstName"
                    label="First Name"
                    variant="outlined"
                    defaultValue={warrantyInputs?.firstName}
                    onChange={handleWarrantyInputs}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    id="lastName"
                    name="lastName"
                    label="Last Name"
                    type="email"
                    variant="outlined"
                    defaultValue={warrantyInputs?.lastName}
                    onChange={handleWarrantyInputs}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    id="warrantyNumber"
                    name="warrantyNumber"
                    label="Warranty Number"
                    type="number"
                    variant="outlined"
                    defaultValue={warrantyInputs?.warrantyNumber}
                    onChange={handleWarrantyInputs}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    id="phoneNumber"
                    name="phoneNumber"
                    label="Phone Number"
                    type="number"
                    variant="outlined"
                    defaultValue={warrantyInputs?.phoneNumber}
                    onChange={handleWarrantyInputs}
                    fullWidth
                  />
                </Grid>
              </>
            )
            : (
              <Grid item xs={12}>
                <Box style={{ height: '24vh', width: '100%' }}>
                  <DataGrid
                    getRowId={
                      (row) => (
                        typeof row.warrantyId === 'string'
                          ? row.warrantyId
                          : String(row.warrantyId))
                    }
                    disableColumnMenu
                    hideFooter
                    loading={!warrantyData}
                    rows={warrantyData || []}
                    columns={columns}
                    density="compact"
                    onSelectionModelChange={(selectedId) => {
                      setWarranty(warrantyData?.find((w) => w.warrantyId === selectedId[0]));
                    }}
                  />
                </Box>
              </Grid>
            )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {showInput
          ? (
            <>
              <Button id="warranty-cancel-modal-button" variant="text" color="secondary" onClick={() => toggleModal(false)}>
                {'Cancel'}
              </Button>
              <Button style={{ minWidth: '8.125rem' }} id="warranty-search-modal-button" variant="contained" color="primary" onClick={() => handleWarrantySearch()} disabled={fetching}>
                {fetching ? <CircularProgress size={25} /> : 'Refine Result'}
              </Button>
            </>
          )
          : (
            <>
              <Button id="warranty-back-modal-button" variant="text" color="secondary" onClick={() => transitionToInput()}>
                {'Back'}
              </Button>
              <Button id="add-warranty-modal-button" variant="contained" color="primary" onClick={handleSaveWarranty}>
                {'Add Warranty'}
              </Button>
            </>
          )}
      </DialogActions>
    </Dialog>
  );
};

export default WarrantyDialog;
